import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

/**
 * UAT Sprint 01 · Finding 5.
 * Edit a user's editable fields (name, phone, role, company, position, status).
 * Uses PATCH /admin/users/:id — the endpoint was added alongside this component.
 */
const ROLE_OPTIONS = [
  { value: 'hm_admin',     label: 'CTH Super Admin' },
  { value: 'hm_advisor',   label: 'CTH Advisor / Consultant' },
  { value: 'client_admin', label: 'Client Admin' },
  { value: 'evaluated',    label: 'Evaluated (Employee/Candidate)' },
];

export default function EditUserDialog({ open, onOpenChange, user: initial, companies = [], onUpdated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '', phone: '', role: 'evaluated', company_id: '__none__',
    position: '', status: 'active', is_employee: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && initial) {
      setForm({
        name: initial.name || '',
        phone: initial.phone || '',
        role: initial.role || 'evaluated',
        company_id: initial.company_id || '__none__',
        position: initial.position || '',
        status: initial.status || 'active',
        is_employee: !!initial.is_employee,
      });
    }
  }, [open, initial]);

  const save = async () => {
    if (!initial) return;
    setSaving(true);
    try {
      const roleChanged = form.role !== initial.role;
      if (roleChanged && !window.confirm(t('pages.users.actions.roleChangeWarn', { defaultValue: 'Changing the role can grant or revoke portal access. Continue?' }))) {
        setSaving(false); return;
      }
      const body = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        role: form.role,
        company_id: form.company_id === '__none__' ? null : form.company_id,
        position: form.position.trim(),
        status: form.status,
        is_employee: form.role === 'evaluated' ? true : !!form.is_employee,
      };
      await adminApi.patchUser(initial.id, body);
      toast.success(t('pages.users.actions.updated', { defaultValue: 'User updated' }));
      onUpdated?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>{t('pages.users.actions.edit', { defaultValue: 'Edit user' })}</DialogTitle>
          <DialogDescription>{initial?.email}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2 max-h-[60vh] overflow-y-auto">
          <div>
            <Label className="text-xs">{t('pages.advisors.nameLabel', { defaultValue: 'Full name' })}</Label>
            <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="edit-user-name" />
          </div>
          <div>
            <Label className="text-xs">{t('pages.advisors.phoneLabel', { defaultValue: 'Phone' })}</Label>
            <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="edit-user-phone" />
          </div>
          <div>
            <Label className="text-xs">{t('pages.users.role', { defaultValue: 'Role' })}</Label>
            <Select value={form.role} onValueChange={(v) => setForm(f => ({ ...f, role: v }))}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t('pages.users.companyOpt', { defaultValue: 'Client / company (optional)' })}</Label>
            <Select value={form.company_id} onValueChange={(v) => setForm(f => ({ ...f, company_id: v }))}>
              <SelectTrigger data-testid="edit-user-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t('pages.users.positionOpt', { defaultValue: 'Position' })}</Label>
            <Input value={form.position} onChange={(e) => setForm(f => ({ ...f, position: e.target.value }))} data-testid="edit-user-position" />
          </div>
          {/* Founder UAT (C) — access role and employment are independent axes. */}
          {form.role !== 'evaluated' && (
            <label className="flex items-start gap-2.5 rounded-md border border-border p-3 cursor-pointer" data-testid="edit-user-is-employee-block">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary"
                checked={!!form.is_employee}
                onChange={(e) => setForm(f => ({ ...f, is_employee: e.target.checked }))}
                data-testid="edit-user-is-employee"
              />
              <span>
                <span className="block text-sm font-medium">{t('phase3.person.isEmployee', { defaultValue: 'Is also an organizational collaborator/employee' })}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{t('phase3.person.isEmployeeHint', { defaultValue: 'Organizational status only — counts as a collaborator and can hold department/position. Grants NO platform permissions.' })}</span>
              </span>
            </label>
          )}
          <div>
            <Label className="text-xs">{t('pages.advisors.statusLabel', { defaultValue: 'Status' })}</Label>
            <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="edit-user-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t('pages.advisors.statusActive', { defaultValue: 'Active' })}</SelectItem>
                <SelectItem value="inactive">{t('pages.advisors.statusInactive', { defaultValue: 'Inactive' })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="edit-user-cancel">
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={save} disabled={saving || !form.name.trim()} className="bg-gradient-primary hover:opacity-90" data-testid="edit-user-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />{t('common.saving', { defaultValue: 'Saving…' })}</> : t('common.save', { defaultValue: 'Save' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
